import { useParams, Link, Navigate } from 'react-router-dom'
import { SEO } from '@/components/SEO'
import { ContactForm } from '@/components/ContactForm'
import { Separator } from '@gjl/ui'
import { posts } from './blog-posts'

// Blog post components
function ReinventPost() {
  return (
    <div className="mx-auto max-w-4xl py-12 text-base leading-relaxed">
      <header className="mb-12 space-y-4">
        <h1 className="text-3xl font-bold text-white sm:text-4xl">What re:Invent Changed About How I Build Software</h1>
        <p className="text-muted-foreground italic">
          Practical AWS and AI engineering patterns for faster delivery, better reliability, and lower costs.
        </p>
        <p className="text-muted-foreground text-sm">
          By Garrett John Law — Software Engineer II @ Re:Build Manufacturing
        </p>
        <p className="text-muted-foreground text-sm">December 5, 2025</p>
        <div className="relative aspect-[16/9] w-full overflow-hidden rounded-lg">
          <img src="/aws-reinvent.jpg" alt="AWS re:Invent 2025" className="h-full w-full object-cover" />
        </div>
        <Separator className="my-4" />
      </header>

      <section className="text-muted-foreground space-y-6">
        <p>
          I just got back from AWS re:Invent in Las Vegas. This post covers the patterns I'm bringing back to my
          team—not the keynote hype, but session-level details that translate into real improvements within 90 days.
        </p>

        <div className="rounded-lg border border-gray-700 bg-gray-800/50 p-4">
          <p className="mb-2 font-semibold text-white">TL;DR</p>
          <ul className="list-inside list-disc space-y-1 text-sm">
            <li>Faster security reviews for AI agents using Bedrock's new policy controls</li>
            <li>Ship AI features only when they beat baseline metrics through automated checks</li>
            <li>Treat model training, fine-tuning, and inference as separate infrastructure problems</li>
            <li>Turn chaos experiments into real disaster recovery playbooks with measurable targets</li>
          </ul>
        </div>

        <h2 className="pt-4 text-2xl font-semibold text-white">Shipping AI Agents Safely</h2>

        <p className="font-medium text-white">Ready to deploy now:</p>
        <ul className="list-inside list-disc space-y-2">
          <li>
            <strong>Bedrock Agent Core Identity and Policy</strong> makes AI agents enterprise-ready. You define exactly
            what tools an agent can access upfront, with strict boundaries. Security reviews go from "let's see what
            this thing can do" to "here's the schema, here are the limits." I've seen these reviews take weeks; this
            cuts that significantly.
          </li>
          <li>
            <strong>Service tiers for inference</strong> separate real-time requests from batch workloads, giving you
            predictable response times for user-facing features while keeping costs down for background processing.
          </li>
        </ul>

        <p className="mt-4 font-medium text-white">Worth piloting next:</p>
        <ul className="list-inside list-disc space-y-2">
          <li>
            <strong>Agent evaluations with observability</strong> looks promising for tracing multi-step agent tasks,
            but needs investment to establish baselines. My plan is to pick one high-volume workflow and see if the
            metrics correlate with user satisfaction before rolling out broadly.
          </li>
        </ul>

        <p className="text-sm italic">
          The main risk: these policies require defining your tool interfaces upfront. If your agent's capabilities are
          still evolving, you'll feel the friction.
        </p>

        <h2 className="pt-4 text-2xl font-semibold text-white">Evaluation-Driven Deployment</h2>

        <p>
          One session stood out: <strong>"AI Evaluation: From Model Testing to Production Monitoring"</strong> at MGM,
          presented by Jessie Manders (Sr. Product Manager) and Sandeep Singh (Sr. Gen AI Data Scientist). They walked
          through AWS Bedrock Evaluations—a managed platform that addresses the evaluation problem that usually takes
          teams weeks or months.
        </p>

        <p>
          The core insight: evaluation is a trade-off between quality, cost, and latency. Most teams optimize for one
          and ignore the others, then wonder why production doesn't match their demos.
        </p>

        <p>Bedrock Evaluations offers three approaches:</p>
        <ul className="list-inside list-disc space-y-2">
          <li>
            <strong>Automated metrics:</strong> Traditional scoring for accuracy and robustness
          </li>
          <li>
            <strong>Human evaluation:</strong> Subject matter experts reviewing answers against a rubric
          </li>
          <li>
            <strong>LLM-as-a-judge:</strong> Using AI to score helpfulness, completeness, readability, and more
          </li>
        </ul>

        <p>
          The practical advice that stuck with me: start with 50-100 of your most common questions. Don't try to
          evaluate everything. Get a subject matter expert to manually review answers—"Is this right? What's
          missing?"—before you automate. And critically: bigger models aren't always better judges.
        </p>

        <p>The deployment loop I'm implementing:</p>
        <ul className="list-inside list-disc space-y-2">
          <li>Evaluation dataset under version control</li>
          <li>Automated checks before merging code</li>
          <li>Gradual rollout in production</li>
          <li>Dashboards tracking success rates and response times</li>
        </ul>

        <p>
          The rule is strict: only ship when the new version beats the current one by agreed-upon margins. No more
          "looks good to me" approvals. This creates auditable decisions and catches regressions before users do.
        </p>

        <p className="text-sm">
          Further reading:{' '}
          <a
            href="https://aws.amazon.com/blogs/machine-learning/evaluate-models-or-rag-systems-using-amazon-bedrock-evaluations-now-generally-available/"
            className="text-primary hover:underline"
            target="_blank"
            rel="noopener noreferrer"
          >
            Bedrock Evaluations announcement
          </a>
          {' | '}
          <a
            href="https://aws.amazon.com/blogs/machine-learning/use-custom-metrics-to-evaluate-your-generative-ai-application-with-amazon-bedrock/"
            className="text-primary hover:underline"
            target="_blank"
            rel="noopener noreferrer"
          >
            Custom metrics guide
          </a>
        </p>

        <h2 className="pt-4 text-2xl font-semibold text-white">
          Training, Tuning, and Inference Are Different Problems
        </h2>

        <p>
          One mental model shift from re:Invent: stop treating ML infrastructure as one thing. Training, fine-tuning,
          and inference have fundamentally different cost drivers and scaling needs:
        </p>

        <ul className="list-inside list-disc space-y-2">
          <li>
            <strong>Training:</strong> Time and data transfer speed dominate. Plan capacity in advance.
          </li>
          <li>
            <strong>Fine-tuning:</strong> Iteration speed matters most. Optimize for fast experiments.
          </li>
          <li>
            <strong>Inference:</strong> Spiky, user-facing, latency-sensitive. Scale based on traffic patterns, not
            averages.
          </li>
        </ul>

        <h2 className="pt-4 text-2xl font-semibold text-white">Resilience That Actually Gets Tested</h2>

        <p>
          Here's how to turn chaos experiments into real disaster recovery playbooks using SSM Inventory, AWS Fault
          Injection Service, and Resilience Hub:
        </p>

        <ul className="list-inside list-disc space-y-2">
          <li>
            <strong>Map your systems first.</strong> Understand what breaks when something fails, before you inject
            failures.
          </li>
          <li>
            <strong>Generate response docs.</strong> Deterministic incident handling, not ad-hoc heroics.
          </li>
          <li>
            <strong>Rehearse recovery.</strong> Measure how long it takes to recover, track improvement over time.
          </li>
        </ul>

        <p>
          The prerequisite most teams skip: accurate resource tagging and inventory. Start there, in non-production
          environments, before you get fancy with fault injection.
        </p>

        <hr className="border-border my-8" />

        <h2 className="text-2xl font-semibold text-white">What I'm Taking Away</h2>

        <p>
          Re:Invent is overwhelming by design—hundreds of sessions, announcements every hour. But the signal is clear:
        </p>

        <p>
          The teams shipping reliable AI features fastest are treating evaluation as a first-class engineering problem,
          not an afterthought. They're defining boundaries for agents upfront. They're segmenting infrastructure by
          workload type. And they're rehearsing failure instead of hoping it doesn't happen.
        </p>

        <p>
          The ideas aren't new, but the tooling to execute them well finally is. The next 90 days will be about putting
          it into practice.
        </p>
      </section>
    </div>
  )
}

// Map of slug to component
const postComponents: Record<string, () => JSX.Element> = {
  'reinvent-2025-software-practices': ReinventPost,
}

export function BlogPost() {
  const { slug } = useParams<{ slug: string }>()

  if (!slug) {
    return <Navigate to="/blog" replace />
  }

  const post = posts.find((p) => p.slug === slug)
  const PostComponent = postComponents[slug]

  if (!post || !PostComponent) {
    return <Navigate to="/blog" replace />
  }

  // Suggested posts: other posts
  const suggested = posts.filter((p) => p.slug !== slug).slice(0, 3)

  return (
    <>
      <SEO
        title={post.title}
        description={post.description}
        type="article"
        url={`https://software.garrettjohnlaw.com/blog/${slug}`}
      />
      <div className="mx-auto space-y-10 bg-gradient-to-b from-gray-950 to-gray-900 py-12">
        <PostComponent />
        <div className="mx-auto max-w-4xl px-6">
          <ContactForm
            header="Let's Connect"
            body="Looking to hire an engineer who ships fast and solves real problems? Send a message below."
            buttonVariant="outline"
          />
        </div>
        {suggested.length > 0 && (
          <section className="mx-auto max-w-4xl space-y-4 px-8">
            <h2 className="text-2xl font-semibold">Suggested Posts</h2>
            <ul className="grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-3">
              {suggested.map((p) => (
                <li key={p.slug} className="flex flex-col items-center gap-4 text-center sm:items-start sm:text-left">
                  <Link
                    to={`/blog/${p.slug}`}
                    className="flex w-full flex-col items-center gap-4 hover:underline sm:items-start"
                  >
                    {p.coverImage && (
                      <div className="relative mx-auto aspect-square w-3/4 overflow-hidden rounded-md sm:mx-0 sm:w-full">
                        <img src={p.coverImage} alt={p.title} className="h-full w-full object-cover" />
                      </div>
                    )}
                    <span className="text-primary font-medium">{p.title}</span>
                  </Link>
                </li>
              ))}
            </ul>
          </section>
        )}
      </div>
    </>
  )
}
